import './Categories.scss';
import { useEffect, useReducer } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useTokenProvider,
  useAuthorizer,
  useConfig,
  useLogger,
} from '@gplibraries/react/hooks';
import { PageTitle,
  LoadingCircle,
  ErrorDisplayComponent,
  ErrorBoundary,
  AlertBox,
  ContentBlock,
  Breadcrumbs,
} from '@gplibraries/react/components';
import { actions, categoriesReducer } from '../../components/CategoryEdit/categoriesReducer.js';
import { fetchDealerResourceCategories, fetchTmResourceCategories } from '@gplibraries/react/api';
import CategoryEditBlock from '../../components/CategoryEdit/CategoryEditBlock.jsx';

// initial categories state
const initialState = {
  initialized: false
}

export default function EditCategories() {

  const [dealerCategories, dispatchDealers] = useReducer(categoriesReducer, initialState)
  const [tmCategories, dispatchTMs] = useReducer(categoriesReducer, initialState)
  const { dealerAccessApiScope, dealerAccessApiHost, drupalScope } = useConfig();
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});
  const getDrupalToken = useTokenProvider({ scopes: [drupalScope]});
  const logger = useLogger();

  // drupal token query
  const { data: drupalToken, isLoading: drupalTokenLoading } = useQuery({
    enabled: Boolean(getDrupalToken),
    queryKey: ['cmsToken'],
    queryFn: () => getDrupalToken()
  });
  const drupalUser = useAuthorizer(drupalToken?.accessTokenClaims);

  const { isLoading: dealerCategoriesLoading, error: dealerCategoriesError, data: dealerCategoriesData } = useQuery({
    enabled: (drupalUser?.hasRole('power_user')),
    queryKey: ['dealer_resource_categories'],
    queryFn: () => getToken()
      .then(token => fetchDealerResourceCategories(dealerAccessApiHost, token.accessToken))
      .then(res => res.json())
  })

  const { isLoading: tmCategoriesLoading, error: tmCategoriesError, data: tmCategoriesData } = useQuery({
    enabled: (drupalUser?.hasRole('power_user')),
    queryKey: ['tm_resource_categories'],
    queryFn: () => getToken()
      .then(token => fetchTmResourceCategories(dealerAccessApiHost, token.accessToken))
      .then(res => res.json())
  })
  const hasError = dealerCategoriesError || tmCategoriesError;


  useEffect(() => {
    if (dealerCategoriesError) {
      logger.error(dealerCategoriesError, {
        source: 'EditCategories',
        action: 'fetch-dealer-categories',
      });
    }

    if (tmCategoriesError) {
      logger.error(tmCategoriesError, {
        source: 'EditCategories',
        action: 'fetch-tm-categories',
      });
    }
  }, [dealerCategoriesError, tmCategoriesError]);


  // when categories are fetched, initialize the categories state
  useEffect(() => {
    try{
      if (dealerCategoriesData?.data && !dealerCategoriesLoading) {
        dispatchDealers({
          type: actions.INITIALIZE,
          payload: dealerCategoriesData.data
        })
      }
      if (tmCategoriesData && !tmCategoriesLoading) {
        dispatchTMs({
          type: actions.INITIALIZE,
          payload: tmCategoriesData.data
        })
      }
    }catch(err){
      logger.error(err, {
        source: 'EditCategories',
        action: 'Initializing categories'
      });
    }
  }, [dealerCategoriesData, tmCategoriesData]);

  const loading = dealerCategoriesLoading || tmCategoriesLoading;

  return (<>

    <Breadcrumbs breadcrumbs={{'Edit Categories': '/categories/edit'}}/>

    <PageTitle>Edit Categories</PageTitle>

    {(loading || drupalTokenLoading) && <LoadingCircle className={'large'}/>}

    {!loading && hasError && (
      <AlertBox type="error">
        Something went wrong while loading categories. Please try again later.
      </AlertBox>
    )}

    {!loading && !hasError &&<>

      <div className="flex-col full categories-container">

        {dealerCategories.initialized &&
          <ContentBlock title={'Dealer Resource Categories'} className={'no-gap'}>
            <ErrorBoundary
              fallback={<ErrorDisplayComponent message={'Dealer resource categories could not be loaded.'}/>}
              logger={logger}
            >

              <CategoryEditBlock
                categories={dealerCategories.categories}
                dispatch={dispatchDealers}
              />

            </ErrorBoundary>
          </ContentBlock>
        }

        {tmCategories.initialized &&
          <ContentBlock title={'TM Resource Categories'}>
            <ErrorBoundary
              fallback={<ErrorDisplayComponent message={'TM resource categories could not be loaded.'}/>}
              logger={logger}
            >

              <CategoryEditBlock
                categories={tmCategories.categories}
                dispatch={dispatchTMs}
              />

            </ErrorBoundary>
          </ContentBlock>
        }

      </div>


    </>}
  </>);

}